/**
 * Alert Config → Watchlist: every person and vehicle this site watches for, READ-ONLY.
 *
 * A watchlist hit needs no alert rule — the list decides (the command-center's alertEvaluator):
 * the entry's category names the alert ("Missing Person"), its severity is the alert's, and the
 * hit is always on the alert board. Who is told is Routing's ("Watchlist" in a notification).
 * The list itself is changed on the Watch List page only; this tab just shows it beside the rules.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowPathIcon, ArrowTopRightOnSquareIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { VehicleBlacklistEntry, WatchlistPerson } from '../../api';
import {
  PERSON_WATCHLIST_CATEGORIES,
  VEHICLE_WATCHLIST_CATEGORIES,
  watchlistCategoryOf,
  watchlistSeverityOf,
  type WatchlistCategory,
} from '../common/watchlistForm';
import { Callout } from '../common/Callout';
import { alertActionsErrorText } from './alertActionsModel';

export interface WatchlistOverviewData {
  people: WatchlistPerson[];
  vehicles: VehicleBlacklistEntry[];
}

export interface WatchlistOverviewProps {
  /** The site's listed people and vehicles. Read when the view opens (remount with `key` to re-read for another site). */
  load: () => Promise<WatchlistOverviewData>;
  /** "Open Watch List" — where the list is changed. Hidden when absent. */
  onManage?: () => void;
}

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

function CategoryChip({ list, value, alertName }: { list: WatchlistCategory[]; value?: string | null; alertName?: boolean }) {
  const c = watchlistCategoryOf(list, value) || list[0];
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${c.chip}`}>
      {alertName ? c.alertName : c.label}
    </span>
  );
}

function SeverityChip({ value }: { value?: string | null }) {
  const s = watchlistSeverityOf(value || 'critical') || watchlistSeverityOf('critical')!;
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${s.chip}`}>{s.label}</span>;
}

const th = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground';
const td = 'px-3 py-2 align-top text-[13px] text-foreground';

export default function WatchlistOverview({ load, onManage }: WatchlistOverviewProps) {
  const [data, setData] = useState<WatchlistOverviewData | null>(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  // Read on open and on Reload only — a host may pass a new `load` every render.
  const loadRef = useRef(load);
  loadRef.current = load;
  const read = useCallback(async () => {
    setError('');
    try {
      setData(await loadRef.current());
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not read the watchlist'));
      setData((d) => d || { people: [], vehicles: [] });
    }
  }, []);
  useEffect(() => { read(); }, [read]);

  const needle = q.trim().toLowerCase();
  const people = useMemo(() => (data?.people || []).filter((p) => !needle
    || [p.name, p.person_id, p.blacklist_reason, p.blacklist_category].some((v) => String(v || '').toLowerCase().includes(needle))), [data, needle]);
  const vehicles = useMemo(() => (data?.vehicles || []).filter((v) => !needle
    || [v.plate_display, v.plate_number, v.reason, v.category].some((x) => String(x || '').toLowerCase().includes(needle))), [data, needle]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-[14rem] flex-1 text-[13px] text-muted-foreground">
          A listed person or vehicle seen on any camera raises an alert by itself — always on the alert board. Who else is told is set under “Who gets notified”.
        </p>
        <label className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a name or plate"
            className="h-8 w-56 rounded-lg border border-border bg-background pl-8 pr-2 text-[13px] text-foreground" />
        </label>
        <button type="button" onClick={read} className="rounded-lg border border-border bg-card p-1.5 text-muted-foreground hover:bg-muted" title="Reload">
          <ArrowPathIcon className="h-4 w-4" />
        </button>
        {onManage && (
          <button type="button" onClick={onManage} className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-2.5 py-1.5 text-[12px] text-foreground hover:bg-muted">
            <ArrowTopRightOnSquareIcon className="h-4 w-4" /> Change on Watch List
          </button>
        )}
      </div>
      {error && <Callout tone="danger">{error}</Callout>}

      <section className="overflow-hidden rounded-xl border border-border bg-card">
        <h3 className="border-b border-border bg-muted px-3 py-2 text-[13px] font-semibold text-foreground">
          People <span className="font-normal text-muted-foreground">({data ? data.people.length : '…'})</span>
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem]">
            <thead><tr><th className={th}>Name</th><th className={th}>Alert</th><th className={th}>Severity</th><th className={th}>Raises an alert</th><th className={th}>Reason</th><th className={th}>Listed</th></tr></thead>
            <tbody className="divide-y divide-border">
              {data === null && <tr><td className={td} colSpan={6}>Loading…</td></tr>}
              {data && people.length === 0 && <tr><td className={`${td} text-muted-foreground`} colSpan={6}>{needle ? 'Nobody matches.' : 'Nobody is on the watchlist.'}</td></tr>}
              {people.map((p) => (
                <tr key={p.person_id}>
                  <td className={td}>{p.name || p.person_id}</td>
                  <td className={td}><CategoryChip list={PERSON_WATCHLIST_CATEGORIES} value={p.blacklist_category} alertName /></td>
                  <td className={td}><SeverityChip value={p.blacklist_severity} /></td>
                  <td className={td}>{p.blacklist_alert === false ? <span className="text-muted-foreground">No — sightings only</span> : 'Yes'}</td>
                  <td className={`${td} max-w-xs text-muted-foreground`}>{p.blacklist_reason || '—'}</td>
                  <td className={`${td} whitespace-nowrap text-muted-foreground`}>{when(p.blacklisted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-border bg-card">
        <h3 className="border-b border-border bg-muted px-3 py-2 text-[13px] font-semibold text-foreground">
          Vehicles <span className="font-normal text-muted-foreground">({data ? data.vehicles.length : '…'})</span>
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem]">
            <thead><tr><th className={th}>Plate</th><th className={th}>Alert</th><th className={th}>Severity</th><th className={th}>Raises an alert</th><th className={th}>Reason</th><th className={th}>Until</th></tr></thead>
            <tbody className="divide-y divide-border">
              {data === null && <tr><td className={td} colSpan={6}>Loading…</td></tr>}
              {data && vehicles.length === 0 && <tr><td className={`${td} text-muted-foreground`} colSpan={6}>{needle ? 'No vehicle matches.' : 'No vehicle is on the watchlist.'}</td></tr>}
              {vehicles.map((v) => (
                <tr key={v.id}>
                  <td className={`${td} font-mono`}>{v.plate_display || v.plate_number}</td>
                  <td className={td}><CategoryChip list={VEHICLE_WATCHLIST_CATEGORIES} value={v.category} alertName /></td>
                  <td className={td}><SeverityChip value={v.severity} /></td>
                  <td className={td}>{v.is_active ? 'Yes' : <span className="text-muted-foreground">No — turned off</span>}</td>
                  <td className={`${td} max-w-xs text-muted-foreground`}>{v.reason || '—'}</td>
                  <td className={`${td} whitespace-nowrap text-muted-foreground`}>{v.expires_at ? when(v.expires_at) : 'Always'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
